import { FC } from "react";
import { ExternalLink } from "lucide-react";
import ContentAvatar, {
  LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE,
  CONTENT_TYPE_COPY,
} from "@src/features/contentLibrary/ContentAvatar";
import LibraryItemRow from "@src/features/libraryItem/module/createModuleContentMembershipMenu/LibraryItemRow";
import type { ChatReferenceLibraryItem } from "@src/features/aiAssist/hooks/useChatReferences";
import { useIsTrainee } from "@src/features/app/hooks/useIsTrainee";
import { FileCitationAnnotation } from "./types";

interface ChatReferenceProps {
  annotation: FileCitationAnnotation;
  libraryItem?: ChatReferenceLibraryItem;
  referenceCount: number;
  citationNumber?: number;
  onClose?: () => void;
}

const getReferenceCountText = (count: number): string => {
  return `${count} reference${count > 1 ? "s" : ""}`;
};

export const ChatReference: FC<ChatReferenceProps> = ({
  annotation,
  libraryItem,
  referenceCount,
  citationNumber,
  onClose,
}) => {
  const { isTrainee } = useIsTrainee();

  const handlePress = () => {
    if (!libraryItem) {
      return;
    }

    // Close any modals before navigating
    onClose?.();

    // For trainees with assignment data, deep-link into the in-progress
    // course/module instance. myAssignment is only populated for items the
    // trainee has a direct Assignment for; folder/automation-only access
    // falls through to the library-item route below.
    const myAssignment = isTrainee ? libraryItem.myAssignment : null;
    if (myAssignment) {
      if (myAssignment.currentCourseInstance) {
        window.open(
          `/my-training/course-instance/${myAssignment.currentCourseInstance.id}`,
          "_blank",
        );
        return;
      } else if (myAssignment.currentModuleInstance) {
        window.open(
          `/my-training/module-instance/${myAssignment.currentModuleInstance.id}`,
          "_blank",
        );
        return;
      }
    }

    // Fallback: open the library item page (works for managers and for
    // trainees accessing standalone training resources via folders).
    window.open(`/library/library-item/${libraryItem.id}`, "_blank");
  };

  if (!libraryItem) {
    // Fallback for when we don't have library item data
    return (
      <LibraryItemRow
        avatar={<div className="size-8 rounded-lg bg-gray-3" />}
        title={annotation.filename || "Unknown Document"}
        subtitle={getReferenceCountText(referenceCount)}
        onClick={() => {}}
        isDisabled={true}
        rowType="Card"
      />
    );
  }

  // Create avatar component
  const avatar = (
    <ContentAvatar
      size="32px"
      defaultBackgroundHashKey={libraryItem.id}
      contentType={LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE[libraryItem.type]}
      coverImage={libraryItem.coverImage ? libraryItem.coverImage : undefined}
      style={{ borderRadius: 8 }}
    />
  );

  // Create title
  const title =
    libraryItem.name.myTranslation || `Untitled ${libraryItem.type}`;

  // Create subtitle with content type and reference info
  const contentTypeLabel =
    CONTENT_TYPE_COPY[LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE[libraryItem.type]];
  const subtitleParts = [contentTypeLabel];

  if (citationNumber) {
    subtitleParts.push(`[${citationNumber}]`);
  }

  subtitleParts.push(getReferenceCountText(referenceCount));
  const subtitle = subtitleParts.join(" • ");

  return (
    <LibraryItemRow
      avatar={avatar}
      title={title}
      subtitle={subtitle}
      onClick={handlePress}
      rowType="Card"
      showRemoveIcon={false}
      rightAdornment={<ExternalLink className="size-4 text-gray-6" />}
    />
  );
};
